import useI18n from "@/hooks/useI18n";
import SheetBackdrop from "@/atomic/atoms/SheetBackdrop";
import { ScraperConsultationItem } from "@/domain/scrapers/consultation";

interface ScraperPassagesDrawerProps {
  item: ScraperConsultationItem | null;
  onClose: () => void;
}

export default function ScraperPassagesDrawer({
  item,
  onClose,
}: ScraperPassagesDrawerProps) {
  const { t } = useI18n();

  if (!item) return null;

  return (
    <SheetBackdrop onClose={onClose} labelledBy="scraper-passages-title">
      <div className="flex flex-col items-center shrink-0">
        <div className="w-11 h-1 bg-border rounded-[10px] mb-2" />
        <button
          type="button"
          onClick={onClose}
          className="self-end"
          aria-label={t("common:close")}
        >
          <img
            src="/svg/icon-close.svg"
            alt=""
            width={24}
            height={24}
            className="w-6 h-6"
          />
        </button>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <span
          aria-hidden="true"
          className="w-6 h-6 p-1 rounded-lg bg-border flex items-center justify-center shrink-0"
        >
          <img
            src="/svg/icon-check.svg"
            alt=""
            width={12}
            height={9}
            className="w-3 h-[9px]"
          />
        </span>
        <h2
          id="scraper-passages-title"
          className="flex-1 text-base font-medium font-secondary text-text tracking-[-0.4px] leading-[20.8px]"
        >
          {item.name}
        </h2>
        <span className="px-1 h-4 rounded bg-info-muted text-info-muted-text text-xs font-medium font-secondary leading-[16.8px] flex items-center">
          {t("pages:home:scrapersPassageCount", {
            count: item.passageCount,
          })}
        </span>
      </div>

      <ul
        data-testid="scraper-passages-scroll"
        className="mt-5 flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-thin-internal flex flex-col gap-4 pr-1"
      >
        {item.passages.map((passage, index) => (
          <li key={`${passage.location}-${index}`} className="flex gap-3.5">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium font-secondary text-tertiary tracking-[-0.4px] leading-[18.2px]">
                {passage.location}
              </p>
              <p className="text-sm font-secondary text-[#696966] tracking-[-0.4px] leading-[18.2px]">
                {passage.dateLabel}
              </p>
            </div>
            <p className="text-sm font-medium font-secondary text-text tracking-[-0.4px] leading-[18.2px]">
              R$ {passage.price}
            </p>
          </li>
        ))}
      </ul>

      {item.paymentUrl ? (
        <div className="shrink-0 pt-5 bg-foreground">
          <a
            href={item.paymentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full h-[42px] rounded-lg border border-text flex items-center justify-center text-sm font-bold font-primary text-text"
          >
            {t("pages:home:scrapersPayOnSite")}
          </a>
        </div>
      ) : null}
    </SheetBackdrop>
  );
}
